import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CategoryIcon from "@mui/icons-material/Category";
import CottageIcon from "@mui/icons-material/Cottage";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InsightsIcon from "@mui/icons-material/Insights";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import LanguageIcon from "@mui/icons-material/Language";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import MenuIcon from "@mui/icons-material/Menu";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person2";
import RoomPreferencesIcon from "@mui/icons-material/RoomPreferences";
import SettingsIcon from "@mui/icons-material/Settings";
import SourceIcon from "@mui/icons-material/Source";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  alpha,
  useMediaQuery,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React, { ReactNode, useEffect, useMemo } from "react";
import { useIdleTimer } from "react-idle-timer";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useGetUserQuery } from "../../api/apiSlice";
import { Avatar } from "../../components/Avatar/Avatar";
import Search from "../../components/Search/Search";
import { useAuth } from "../../hooks";
import LogoIcon from "../../images/logo.svg";
import { Role, UserPermissions, canAccess, getDefaultRoute } from "../../utils/permissions";
import { UserRole } from "../../utils/types";
import AccountMenu from "./AccountMenu";

const drawerWidth = 280;

interface MenuItem {
  label: string;
  icon: ReactNode;
  path: string;
  visible?: boolean;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

const Main = styled("main", {
  shouldForwardProp: prop => !(["open", "isMobile"] as any).includes(prop),
})<{
  open?: boolean;
  isMobile?: boolean;
}>(({ theme, open, isMobile }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${isMobile ? 0 : drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: `${isMobile ? 0 : drawerWidth}px`,
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  width: "100%",
}));

interface Props {
  children: React.ReactNode;
  requiredPermission?: UserPermissions;
}

// Temporarily hide search bar for now. Not required yet.
const showSearchComponent = false;

const REFRESH_TOKEN_TIMING_MS = 1000 * 60 * 10; // 10 minutes in milliseconds
const IDLE_TIMEOUT_MS = 1000 * 60 * 30; // 30 minutes in milliseconds
const PROMPT_BEFORE_IDLE_MS = 30000; // 30 seconds in milliseconds

const IS_DEV = process.env.NODE_ENV === "development";
// TODO: Remove this dev-only gate when Content Performance is ready for the main nav.
const showContentPerformanceNav = IS_DEV;

function Wrapper(props: Props): JSX.Element {
  const { children, requiredPermission } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = React.useState(!isMobile);
  const [openIdleTimeoutModal, setOpenIdleTimeoutModal] = React.useState(false);

  const { logoutAction, user, refreshTokenAction, refreshTokenResult, accessToken, tokenExpiry } = useAuth();

  const { data: userData, isLoading: isUserLoading } = useGetUserQuery({});
  const userRoles: string[] = useMemo(() => userData?.roles?.map((role: UserRole) => role.role) || [], [userData]);

  const can = (permission: UserPermissions) => canAccess(userRoles as Role[], permission);

  const defaultRoute = useMemo(() => getDefaultRoute(userRoles), [userRoles]);

  const navigate = useNavigate();

  // Permission-based route protection: redirect unauthorized users imperatively.
  // Using useEffect + navigate() instead of <Navigate> to avoid a full component tree
  // unmount/remount which causes a long blank page in slow environments.
  const hasAccess = !requiredPermission || can(requiredPermission);
  const shouldRedirect = !isUserLoading && requiredPermission && !hasAccess;

  useEffect(() => {
    if (shouldRedirect) {
      const defaultRoute = getDefaultRoute(userRoles);
      navigate(defaultRoute, { replace: true });
    }
  }, [shouldRedirect, userRoles, navigate]);

  const onPrompt = () => {
    setOpenIdleTimeoutModal(true);
  };

  const onIdle = () => {
    setOpenIdleTimeoutModal(false);
    logoutAction();
  };

  const onActive = (event: any) => {
    setOpenIdleTimeoutModal(false);
  };

  const { reset } = useIdleTimer({
    onPrompt,
    onIdle,
    onActive,
    // Automatically log out the user after 30 minutes of inactivity
    timeout: IDLE_TIMEOUT_MS,
    // Alert the user 30 seconds before automatic logout due to inactivity
    promptBeforeIdle: PROMPT_BEFORE_IDLE_MS,
  });

  useEffect(() => {
    if (!isMobile) {
      setOpen(true);
    }
  }, [isMobile]);

  const handleCloseIdleModal = () => {
    setOpenIdleTimeoutModal(false);
    reset();
  };

  const handleAcceptLogoutNow = () => {
    setOpenIdleTimeoutModal(false);
    logoutAction();
  };
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const menuSections: MenuSection[] = [
    {
      title: "Overview",
      items: [
        {
          label: "Dashboard",
          icon: <DashboardIcon color="primary" />,
          path: "/dashboard",
          visible: can(UserPermissions.VIEW_DASHBOARD),
        },
        {
          label: "Content Performance",
          icon: <InsightsIcon color="primary" />,
          path: "/content-performance",
          visible: showContentPerformanceNav && can(UserPermissions.VIEW_CONTENT_PERFORMANCE),
        },
        {
          label: "Users",
          icon: <PeopleIcon color="primary" />,
          path: "/users",
          visible: can(UserPermissions.VIEW_USERS_LIST),
        },
      ],
    },
    {
      title: "Content",
      items: [
        {
          label: "Quotes",
          icon: <FormatQuoteIcon color="primary" />,
          path: "/quotes",
          visible: can(UserPermissions.VIEW_PUBLISHED_QUOTES),
        },
        {
          label: "Cultures",
          icon: <CottageIcon color="primary" />,
          path: "/cultures",
          visible: can(UserPermissions.VIEW_PUBLISHED_CULTURES),
        },
        {
          label: "Culture Categories",
          icon: <CategoryIcon color="primary" />,
          path: "/culture-categories",
          visible: can(UserPermissions.VIEW_CULTURE_CATEGORIES),
        },
        {
          label: "Authors",
          icon: <PersonIcon color="primary" />,
          path: "/authors",
          visible: can(UserPermissions.VIEW_PUBLISHED_AUTHORS),
        },
        {
          label: "Sources",
          icon: <SourceIcon color="primary" />,
          path: "/sources",
          visible: can(UserPermissions.VIEW_PUBLISHED_SOURCES),
        },
        {
          label: "Tags",
          icon: <LocalOfferIcon color="primary" />,
          path: "/tags",
          visible: can(UserPermissions.VIEW_PUBLISHED_TAGS),
        },
        {
          label: "Languages",
          icon: <LanguageIcon color="primary" />,
          path: "/languages",
          visible: can(UserPermissions.VIEW_PUBLISHED_LANGUAGES),
        },
        {
          label: "References",
          icon: <RoomPreferencesIcon color="primary" />,
          path: "/references",
          visible: can(UserPermissions.VIEW_PUBLISHED_REFERENCES),
        },
      ],
    },
    {
      title: "System",
      items: [
        {
          label: "Settings",
          icon: <SettingsIcon color="primary" />,
          path: "/settings",
          visible: can(UserPermissions.MANAGE_SETTINGS),
        },
        {
          label: "System Health",
          icon: <MonitorHeartIcon color="primary" />,
          path: "/system-health",
          visible: can(UserPermissions.VIEW_SYSTEM_HEALTH),
        },
      ],
    },
  ];

  const menuItems: MenuItem[] = menuSections.flatMap(section => section.items);

  const mainSections = menuSections.slice(0, 2);
  const bottomSection = menuSections[2];
  const visibleBottomItems = bottomSection.items.filter(item => item.visible);

  const { pathname } = useLocation();
  const currentPath = menuItems.find(({ path, visible }) => path?.toLowerCase() === pathname?.toLowerCase() && visible);

  useEffect(() => {
    if (accessToken) {
      const currentTime = Date.now();
      const refreshThreshold = currentTime + REFRESH_TOKEN_TIMING_MS;
      const tokenExpiryTime = tokenExpiry * 1000;

      // Refresh token 10 minutes before expiry
      if (refreshThreshold > tokenExpiryTime) {
        refreshTokenAction();
      }
    }
  }, [accessToken, refreshTokenAction, tokenExpiry]);

  if (refreshTokenResult.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }
  if (!accessToken) {
    return <Navigate to="/" replace />;
  }

  // Only show inline loader when we actually need user data for a permission check,
  // or when a redirect is pending. Unrestricted routes render children immediately.
  const showInlineLoader = (requiredPermission && isUserLoading) || shouldRedirect;

  return (
    <Box>
      <Dialog
        open={openIdleTimeoutModal}
        onClose={handleCloseIdleModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Are you still there?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You have been idle for more 30 minutes. You will be logged out in 30 seconds. Do you want to stay logged in?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" sx={{ border: "1px solid white" }} onClick={handleCloseIdleModal} color="primary">
            <Typography color="textPrimary">Stay logged in</Typography>
          </Button>
          <Button onClick={handleAcceptLogoutNow} color="secondary" autoFocus>
            Log out now
          </Button>
        </DialogActions>
      </Dialog>
      <AppBar
        sx={theme => ({
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: "row",
          background: theme.palette.background.default,
          zIndex: theme.zIndex.drawer + 1,
        })}
      >
        <Box component={Link} to={defaultRoute} display="flex" alignItems="center" sx={{ textDecoration: "none" }} gap={4}>
          <img alt="logo" src={LogoIcon} />
          <Typography color="textPrimary" variant="subtitle2">
            WorldWise
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" sx={{ flex: 1, width: "100%" }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: "none" }) }}
          >
            <MenuIcon />
          </IconButton>
          {/* <Typography variant="subtitle1">{currentPath?.label}</Typography> */}
        </Box>
        {showSearchComponent && <Search />}
        <Box display="flex" alignItems="center">
          <AccountMenu accountMenuTooltipTitle={user?.name || ""}>
            <Avatar name={user?.name || ""} />
          </AccountMenu>
        </Box>
        {/* </Toolbar> */}
      </AppBar>
      <Drawer
        sx={theme => ({
          width: drawerWidth,
          flexShrink: 0,
          position: "relative",
          background: alpha(theme.palette.primary.main, 0.12),
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            background: !isMobile ? alpha(theme.palette.primary.main, 0.12) : theme.palette.background.paper,
          },
        })}
        variant={isMobile ? undefined : "persistent"}
        anchor="left"
        open={open}
      >
        <DrawerHeader
          sx={{
            p: 2,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" flex={1}>
            <Box component={Link} to={defaultRoute} display="flex" sx={{ textDecoration: "none" }} alignItems="center" flex={1} gap={4}>
              <img alt="logo" src={LogoIcon} />
              <Typography variant="subtitle2">WorldWise</Typography>
            </Box>
            {isMobile && (
              <IconButton onClick={handleDrawerClose}>{theme.direction === "ltr" ? <ChevronLeftIcon /> : <ChevronRightIcon />}</IconButton>
            )}
          </Box>
        </DrawerHeader>
        {/* <Divider /> */}
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", justifyContent: "space-between" }}>
          <Box>
            <List sx={{ p: 2 }}>
              {mainSections.map(section => {
                const visibleItems = section.items.filter(item => item.visible);
                if (!visibleItems.length) return null;

                return (
                  <Box key={section.title} sx={{ mb: 2.5 }}>
                    <Typography
                      variant="caption"
                      sx={theme => ({
                        px: 1,
                        mb: 1,
                        display: "block",
                        color: alpha(theme.palette.text.primary, 0.6),
                        letterSpacing: 0.8,
                        textTransform: "uppercase",
                      })}
                    >
                      {section.title}
                    </Typography>
                    {visibleItems.map(({ label, icon, path }) => (
                      <ListItem sx={{ mb: 1.2 }} key={path} disablePadding>
                        {/* @ts-ignore*/}
                        <ListItemButton
                          sx={theme => ({
                            boxSizing: "border-box",
                            border: "1px solid transparent",
                            color: theme.palette.primary.main,
                            ...(currentPath?.path === path && {
                              background: theme.palette.background.default,
                              border: `1px solid ${theme.palette.primary.main}`,
                              borderRadius: 1,
                            }),
                            height: 40,
                            "&:hover": {
                              background: currentPath?.path === path ? theme.palette.background.default : "transparent",
                              border: `1px solid ${theme.palette.primary.main}`,
                              borderRadius: 1,
                            },
                          })}
                          LinkComponent={Link}
                          to={path}
                        >
                          <ListItemIcon>{icon}</ListItemIcon>
                          <ListItemText primary={label} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </Box>
                );
              })}
            </List>
          </Box>
          {visibleBottomItems.length > 0 ? (
            <Box sx={{ px: 2, pb: 2 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography
                variant="caption"
                sx={theme => ({
                  px: 1,
                  mb: 1,
                  display: "block",
                  color: alpha(theme.palette.text.primary, 0.6),
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                })}
              >
                {bottomSection.title}
              </Typography>
              <List sx={{ p: 0 }}>
                {visibleBottomItems.map(({ label, icon, path }) => (
                  <ListItem sx={{ mb: 1.2 }} key={path} disablePadding>
                    {/* @ts-ignore*/}
                    <ListItemButton
                      sx={theme => ({
                        boxSizing: "border-box",
                        border: "1px solid transparent",
                        color: theme.palette.primary.main,
                        ...(currentPath?.path === path && {
                          background: theme.palette.background.default,
                          border: `1px solid ${theme.palette.primary.main}`,
                          borderRadius: 1,
                        }),
                        height: 40,
                        "&:hover": {
                          background: currentPath?.path === path ? theme.palette.background.default : "transparent",
                          border: `1px solid ${theme.palette.primary.main}`,
                          borderRadius: 1,
                        },
                      })}
                      LinkComponent={Link}
                      to={path}
                    >
                      <ListItemIcon>{icon}</ListItemIcon>
                      <ListItemText primary={label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : null}
          {/* Uncomment this to allow all users to be able to see all menus */}
          {/* {!isAdminOrApprover && (
            <Box sx={{ p: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={showMenusForAllRoles} onChange={handleCheckboxChange} />}
                label="Show all menus"
              />
            </Box>
          )} */}
        </Box>

        {/* <Divider /> */}
        <Box sx={{ position: "absolute", bottom: 0, width: "100%" }}>
          <Divider />
        </Box>
      </Drawer>
      <Main sx={{ py: 2, px: { xs: 4, sm: 8 } }} open={open} isMobile={isMobile}>
        <DrawerHeader sx={{ mb: 4 }} />
        <Typography variant="subtitle1">{currentPath?.label}</Typography>
        {showInlineLoader ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          children
        )}
      </Main>
    </Box>
  );
}

export default Wrapper;
